import React, { lazy } from "react";
import { Route } from "react-router-dom";
import MainLayout from "../../components/MainLayout/MainLayout";
import { AuthenticationGuard } from "../../components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";

// Lazy-loaded components
const ProductDirectory = lazy(() => import("@/modules/productStorefront/features/ProductDirectory/components/ProductDirectory/ProductDirectory"));
const ProductDetails = lazy(() => import("@/modules/productStorefront/features/ProductDirectory/components/ProductDetails/ProductDetails"));
const ProductCheckout = lazy(() => import("@/modules/productStorefront/features/ProductCheckout/components/ProductCheckout/ProductCheckout"));
const ProductConfirmation = lazy(() => import("@/modules/productStorefront/features/ProductCheckout/components/ProductConfirmation/ProductConfirmation"));
const ProductList = lazy(() => import("@/modules/productStorefront/features/ProductManagement/components/ProductList/ProductList"));
const CreateProduct = lazy(() => import("@/modules/productStorefront/features/ProductManagement/components/CreateProduct/CreateProduct"));
const EditProduct = lazy(() => import("@/modules/productStorefront/features/ProductManagement/components/EditProduct/EditProduct"));
const ProductCategoryList = lazy(() => import("@/modules/productStorefront/features/ProductCategoryManagement/components/ProductCategoryList/ProductCategoryList"));
const OrderList = lazy(() => import("@/modules/productStorefront/features/ProductOrderManagement/components/OrderList/OrderList"));
const OrderDetails = lazy(() => import("@/modules/productStorefront/features/ProductOrderManagement/components/OrderDetails/OrderDetails"));
const ProductReport = lazy(() => import("@/modules/productStorefront/features/ProductReport/components/ProductReport/ProductReport"));

// Create stable component reference
const ProductStorefrontMainLayout = () => (
  <MainLayout section="product-storefront" />
);

const ProductStorefrontRoutes = (
  <Route
    element={<AuthenticationGuard component={ProductStorefrontMainLayout} />}
    path="/product-storefront"
  >
    {/* Public product browsing */}
    <Route element={<ProductDirectory />} path="products" />
    <Route element={<ProductDetails />} path="products/:uuid" />

    {/* Checkout flow */}
    <Route element={<ProductCheckout />} path="checkout" />
    <Route element={<ProductConfirmation />} path="checkout/confirmation" />

    {/* Admin: Product management */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ProductList />
        </PermissionGuard>
      }
      path="admin/products"
    />
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <CreateProduct />
        </PermissionGuard>
      }
      path="admin/products/new"
    />
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <EditProduct />
        </PermissionGuard>
      }
      path="admin/products/:uuid/edit"
    />

    {/* Admin: Product categories */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ProductCategoryList />
        </PermissionGuard>
      }
      path="admin/product-categories"
    />

    {/* Admin: Order management / fulfillment */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <OrderList />
        </PermissionGuard>
      }
      path="admin/orders"
    />
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <OrderDetails />
        </PermissionGuard>
      }
      path="admin/orders/:uuid"
    />

    {/* Admin: Reports */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <ProductReport />
        </PermissionGuard>
      }
      path="admin/reports"
    />
  </Route>
);

export default ProductStorefrontRoutes;
